// The Usage demo's fixtures: the two deals the opportunity reports are about,
// their projects and the needs a started negotiation has to fulfil.
// Figures match plans/usage-demo-reports/*.layout.json and
// plans/usage-demo-script.md.

import { logoDevUrlForWebsite } from "../../../../common/LogoDev";
import { int, money } from "./format";
import type { Need, Person, UsageDeal, UsageProject, UsageVendor } from "./types";

const pad = (n: number) => String(n).padStart(2, "0");
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** The demo's today: the real date, so every deadline keeps its distance whenever the demo runs. */
export const DEMO_TODAY = iso(new Date());

/** The date n days from today, ISO; the fixtures' dates are all set this way. */
export const inDays = (n: number): string => {
    const d = new Date();
    d.setDate(d.getDate() + n);
    return iso(d);
};

/** "February": the month a date falls in, for the prose. */
export const monthName = (isoDate: string): string =>
    new Date(`${isoDate}T00:00:00Z`).toLocaleString("en-US", { month: "long", timeZone: "UTC" });

/** The signed-in user, negotiation owner of both deals. */
export const ME = "Rachel Sato";

/** The customer's directory, as the delegate picker offers it. */
export const PEOPLE: Person[] = [
    { name: "Marcus Oyelaran", role: "Head of Operations" },
    { name: "Dana Whitfield", role: "Head of Client services" },
    { name: "Lena Graf", role: "Head of Advisory and wealth" },
    { name: "Jonas Mills", role: "CTO office, Technology" },
    { name: "Adrian Meyer", role: "Corporate functions" },
    { name: "Priya Kapoor", role: "Analytics lead" },
    { name: "Tom Hale", role: "Data platform owner" },
    { name: "Sofia Reyes", role: "Microsoft 365 administrator" },
    { name: "Ethan Brooks", role: "FinOps" },
    { name: "Hannah Lindqvist", role: "Legal, commercial contracts" },
    { name: "Omar Haddad", role: "CIO office" },
];

export const VENDORS: UsageVendor[] = [
    { id: "microsoft", name: "Microsoft", website: "microsoft.com" },
    { id: "snowflake", name: "Snowflake", website: "snowflake.com" },
];
for (const vendor of VENDORS) vendor.hero = logoDevUrlForWebsite(vendor.website);

export const DEALS: UsageDeal[] = [
    {
        id: "microsoft-ea",
        vendorId: "microsoft",
        name: "Microsoft EA (E5 and add-ons)",
        type: "seat",
        annualCost: 8_500_000,
        term: { start: inDays(-942), renews: inDays(152), noticeBy: inDays(62) },
    },
    {
        id: "snowflake-capacity",
        vendorId: "snowflake",
        name: "Snowflake capacity commitment",
        type: "consumption",
        annualCost: 6_000_000,
        term: { start: inDays(-198), renews: inDays(166), noticeBy: inDays(107) },
    },
];

/** A function owner's confirmation on the Microsoft EA: of the inactive seats, how many release. */
const seats = (
    id: string,
    fn: string,
    owner: string,
    total: number,
    suggested: number,
    groups: [string, number][]
): Need => ({
    id,
    title: `Confirm the ${fn} seats (${int(total)})`,
    why: `${int(total)} seats in ${fn} have not signed in for 90 days; the owner says how many can go`,
    question: `Of the ${int(total)} ${fn} seats that have not signed in for 90 days, how many can go?`,
    answer: {
        kind: "count",
        total,
        summary: groups.map(([label, count]) => ({ label, count })),
        suggested,
        result: (n) => `${int(n)} of ${int(total)} confirmed for release`,
    },
    suggest: { mode: "delegate", to: owner, due: inDays(22) },
    waived: `Assume the ${int(total)} inactive ${fn} seats all release`,
    ask: `${ME} asks you to confirm ${int(total)} Microsoft 365 seats in ${fn} that have not signed in for 90 days.`,
    due: inDays(22),
});

/** What a suspended development or test warehouse saves a year, on average. */
const PER_ENVIRONMENT = 55_000;
const SNOWFLAKE_OVERAGE = 1_100_000;

export const PROJECTS: UsageProject[] = [
    {
        id: "usage-microsoft",
        name: "EA renewal",
        vendorId: "microsoft",
        dealId: "microsoft-ea",
        reportedOn: inDays(-1),
        trigger: "8,900 of 12,400 licenses active in 90 days; notice in 62 days",
        atStake: { low: 2_400_000, high: 3_400_000 },
        utilization: { value: 0.72, detail: "8,900 of 12,400 licenses active in the last 90 days" },
        report: "OPPORTUNITY_MICROSOFT",
        decisions: {
            request: {
                label: "Start negotiation",
            },
            decline: { label: "Let it renew as is", kind: "renew" },
            snooze: { label: "Remind me later", until: inDays(32) },
        },
        notification: {
            title: "Microsoft EA: 28% of your seats idle, notice in 62 days",
            signal: { value: "28% idle seats", detail: "8,900 of 12,400 active in 90 days" },
            reason: "Renewal approaching; notice by 31 Dec 2026.",
        },
        needs: [
            {
                id: "e5-report",
                title: "E5 feature usage by user",
                why: "Says which of the 2,100 candidates for E3 use an E5-only feature",
                answer: { kind: "document" },
                suggest: { mode: "pull" },
                pullable: "From the Microsoft 365 admin center, within two days",
                waived: "Assume no E5-only use among the candidates",
                ask: `${ME} asks you for the E5 feature usage report by user from the Microsoft 365 admin center.`,
                due: inDays(7),
            },
            {
                id: "copilot",
                title: "Is Copilot part of this renewal?",
                why: "460 of the 1,000 Copilot seats are inactive; in scope, they join the ask",
                question:
                    "Is Copilot part of this renewal? 460 of its 1,000 seats are inactive; in scope, they join the ask.",
                answer: {
                    kind: "choice",
                    options: [
                        "Yes, the 1,000 seats are in scope",
                        "No, Copilot stays on its own agreement",
                    ],
                    suggested: "Yes, the 1,000 seats are in scope",
                },
                suggest: { mode: "answer", value: "Yes, the 1,000 seats are in scope" },
                waived: "Assume Copilot stays out of scope",
                ask: `${ME} asks you whether Copilot is part of the Microsoft EA renewal.`,
                due: inDays(7),
            },
            seats("ops", "Operations", "Marcus Oyelaran", 640, 470, [
                ["Departed staff still holding a license", 120],
                ["No sign-in for more than 120 days", 350],
                ["On leave", 40],
                ["Shared devices", 30],
                ["Other", 100],
            ]),
            seats("cs", "Client services", "Dana Whitfield", 510, 470, [
                ["Departed staff still holding a license", 110],
                ["No sign-in for more than 120 days", 330],
                ["Seasonal staff", 40],
                ["Other", 30],
            ]),
        ],
        deliverable: {
            name: "the playbook",
            due: inDays(46),
            report: "PLAYBOOK_MICROSOFT",
            deliveredOn: inDays(7),
            proposalOn: inDays(21),
            revisedOn: inDays(14),
            progress: (confirmed, pending) =>
                `${int(confirmed)} seats confirmed for release, ${int(pending)} pending with the owners; the recommended quantity of 10,400 holds.`,
        },
    },
    {
        id: "usage-snowflake",
        name: "Capacity commitment review",
        vendorId: "snowflake",
        dealId: "snowflake-capacity",
        reportedOn: inDays(-1),
        trigger: "118% of the commitment pace; $1.1M overage ahead",
        atStake: { low: SNOWFLAKE_OVERAGE },
        utilization: {
            value: 1.18,
            detail: "Consumption against the commitment, at the current pace",
        },
        report: "OPPORTUNITY_SNOWFLAKE",
        decisions: {
            request: {
                label: "Start negotiation",
            },
            decline: { label: "Growth is planned, dismiss", kind: "dismiss" },
            snooze: { label: "Remind me later", until: inDays(73) },
        },
        notification: {
            title: "Snowflake: consumption at 118% of the commitment, $1.1M overage ahead",
            signal: {
                value: "118% pace",
                detail: `Of the commitment; gone in ${monthName(inDays(121))}`,
            },
            reason: "Identified by us from the billing feed; every credit past the commitment costs 20% more.",
        },
        needs: [
            {
                id: "environments",
                title: "The development and test environments, with their owners",
                why: "The 14 warehouses without auto-suspend, to size the rebalancing",
                answer: { kind: "document" },
                suggest: { mode: "pull" },
                pullable: "From ACCOUNT_USAGE, within a day",
                waived: "Assume the 14 environments from the billing feed, no owners",
                ask: `${ME} asks you for the list of Snowflake development and test environments with their owners.`,
                due: inDays(5),
            },
            {
                id: "migration",
                title: "Is the analytics migration that grows the consumption budgeted?",
                why: "Planned growth belongs in the next commitment; unplanned growth is waste to cut",
                question:
                    "Is the analytics migration that grows the consumption budgeted? Planned growth belongs in the next commitment.",
                answer: {
                    kind: "choice",
                    options: ["Yes, it is budgeted", "No", "I do not know"],
                    suggested: "I do not know",
                },
                suggest: { mode: "delegate", to: "Priya Kapoor", due: inDays(11) },
                waived: "Assume the growth is unplanned",
                ask: `${ME} asks you whether the analytics migration that grows Snowflake consumption is budgeted.`,
                due: inDays(11),
            },
            {
                id: "devtest",
                title: "Confirm the development and test environments (14)",
                why: "14 warehouses run without auto-suspend; the owner says which can be suspended when idle",
                question:
                    "Of the 14 development and test warehouses running without auto-suspend, how many can be suspended when idle?",
                answer: {
                    kind: "count",
                    total: 14,
                    summary: [
                        { label: "CI runners", count: 3 },
                        { label: "Feature environments", count: 8 },
                        { label: "Sandboxes", count: 3 },
                    ],
                    suggested: 11,
                    result: (n) =>
                        `${n} of 14 to be suspended when idle, about ${money(n * PER_ENVIRONMENT)} a year`,
                },
                suggest: { mode: "delegate", to: "Tom Hale", due: inDays(11) },
                waived: "Assume all 14 environments can be suspended when idle",
                ask: `${ME} asks you about 14 Snowflake development and test environments running without auto-suspend.`,
                due: inDays(11),
            },
        ],
        deliverable: {
            name: "the early renewal playbook",
            due: inDays(70),
            progress: (suspended) =>
                `Projected overage ${money(Math.max(0, SNOWFLAKE_OVERAGE - suspended * PER_ENVIRONMENT))} instead of $1.1M; a $7.5M term signed in ${monthName(inDays(73))} prices the remaining months at the capacity rate.`,
        },
    },
];
